import { useState, useEffect } from "react";
import axios from "axios";
import {
  LayoutDashboard,
  ShoppingCart,
  Users,
  Package,
  Tag,
  TrendingUp,
  IndianRupee,
  Clock,
  CheckCircle2,
  XCircle,
  Truck,
  RefreshCw,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── helpers ────────────────────────────────────────────────────────────────
const fmt = (n) =>
  new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(n);

const fmtRupee = (n) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(n);

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

// ── status config ─────────────────────────────────────────────────────────
const STATUS_CONFIG = {
  scheduled:        { label: "Scheduled",        color: "#6366f1", bg: "bg-indigo-50", text: "text-indigo-700", icon: Clock },
  pending:          { label: "Pending",          color: "#f59e0b", bg: "bg-amber-50",   text: "text-amber-700",  icon: Clock },
  confirmed:        { label: "Confirmed",        color: "#3b82f6", bg: "bg-blue-50",    text: "text-blue-700",   icon: CheckCircle2 },
  processing:       { label: "Processing",       color: "#8b5cf6", bg: "bg-violet-50",  text: "text-violet-700", icon: RefreshCw },
  out_for_delivery: { label: "Out for Delivery", color: "#0A4CAE", bg: "bg-sky-50",     text: "text-sky-700",    icon: Truck },
  delivered:        { label: "Delivered",        color: "#22c55e", bg: "bg-green-50",   text: "text-green-700",  icon: CheckCircle2 },
  cancelled:        { label: "Cancelled",        color: "#ef4444", bg: "bg-red-50",     text: "text-red-700",    icon: XCircle },
};

// ── stat card ─────────────────────────────────────────────────────────────
const StatCard = ({ icon: Icon, label, value, sub, iconBg, iconColor }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm flex items-center gap-4">
    <div className={`p-3 rounded-xl ${iconBg} flex-shrink-0`}>
      <Icon size={22} className={iconColor} />
    </div>
    <div className="min-w-0">
      <p className="text-sm text-gray-500 truncate">{label}</p>
      <p className="text-2xl font-bold text-gray-900 mt-0.5">{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
    </div>
  </div>
);

const StatSection = ({ title, columns, children }) => (
  <section className="mb-6">
    <h2 className="text-sm font-bold uppercase tracking-wide text-gray-500 mb-3">{title}</h2>
    <div className={`grid grid-cols-1 sm:grid-cols-2 ${columns} gap-4`}>
      {children}
    </div>
  </section>
);

// ── status badge ──────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || { label: status, bg: "bg-gray-100", text: "text-gray-600" };
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${cfg.bg} ${cfg.text}`}>
      {cfg.label}
    </span>
  );
};

// ── main component ────────────────────────────────────────────────────────
const DashboardPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [trendRange, setTrendRange] = useState("7_days");

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("gasman_token");
      const res = await axios.get(`${API}/admin/dashboard`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (e) {
      console.error("Dashboard fetch error", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-9 w-9 border-b-2 border-[#0A4CAE]" />
      </div>
    );
  }

  if (!data) return null;

  // Pie data for order statuses
  const pieData = Object.entries(STATUS_CONFIG).map(([k, config]) => ({
      name: STATUS_CONFIG[k]?.label || k,
      value: data.status_counts?.[k] || 0,
      color: config.color,
    }));
  const hasOrderStatusData = pieData.some((item) => item.value > 0);

  const trendData = data.order_trends?.[trendRange] || data.daily_orders || [];
  const userPieData = [
    { name: "Users", value: data.user_distribution?.users || 0, color: "#0A4CAE" },
    { name: "Agency Users", value: data.user_distribution?.agency_users || 0, color: "#8b5cf6" },
    { name: "Delivery Users", value: data.user_distribution?.delivery_users || 0, color: "#f59e0b" },
    { name: "Blocked Users", value: data.user_distribution?.blocked_users || 0, color: "#ef4444" },
  ].filter((item) => item.value > 0);

  return (
    <div data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 flex items-center gap-3">
            <LayoutDashboard className="text-[#0A4CAE]" size={28} />
            Dashboard
          </h1>
          <p className="text-gray-500 mt-1 text-sm">Live snapshot of your business</p>
        </div>
        <button
          onClick={fetchDashboard}
          className="flex items-center gap-2 text-sm text-gray-500 hover:text-[#0A4CAE] transition-colors"
        >
          <RefreshCw size={15} />
          Refresh
        </button>
      </div>

      {/* ── KPI Groups ── */}
      <div className="mb-8">
        <StatSection title="Order Stats" columns="xl:grid-cols-4">
          <StatCard icon={ShoppingCart} label="Total Orders" value={fmt(data.total_orders)} iconBg="bg-[#e8eef9]" iconColor="text-[#0A4CAE]" />
          <StatCard icon={TrendingUp} label="Orders This Month" value={fmt(data.orders_this_month)} iconBg="bg-violet-50" iconColor="text-violet-600" />
          <StatCard icon={CheckCircle2} label="Delivered Orders" value={fmt(data.status_counts.delivered || 0)} iconBg="bg-green-50" iconColor="text-green-600" />
          <StatCard icon={XCircle} label="Cancelled Orders" value={fmt(data.cancelled_orders || 0)} iconBg="bg-red-50" iconColor="text-red-600" />
        </StatSection>

        <StatSection title="Revenue Stats" columns="xl:grid-cols-3">
          <StatCard icon={IndianRupee} label="Total Revenue" value={fmtRupee(data.revenue_total)} iconBg="bg-green-50" iconColor="text-green-600" />
          <StatCard icon={IndianRupee} label="Revenue This Month" value={fmtRupee(data.revenue_this_month)} iconBg="bg-emerald-50" iconColor="text-emerald-600" />
          <StatCard icon={IndianRupee} label="Cancelled Orders Revenue" value={fmtRupee(data.cancelled_revenue || 0)} iconBg="bg-rose-50" iconColor="text-rose-600" />
        </StatSection>

        <StatSection title="Master Stats" columns="xl:grid-cols-2">
          <StatCard icon={Tag} label="Total Categories" value={fmt(data.total_categories)} iconBg="bg-amber-50" iconColor="text-amber-500" />
          <StatCard icon={Package} label="Total Products" value={fmt(data.total_products)} iconBg="bg-pink-50" iconColor="text-pink-500" />
        </StatSection>

        <StatSection title="User Stats" columns="xl:grid-cols-6">
          <StatCard icon={Users} label="Total Users" value={fmt(data.total_users)} iconBg="bg-orange-50" iconColor="text-orange-500" />
          <StatCard icon={Users} label="Agency Users" value={fmt(data.agency_users || 0)} iconBg="bg-violet-50" iconColor="text-violet-600" />
          <StatCard icon={XCircle} label="Blocked Users" value={fmt(data.blocked_users || 0)} iconBg="bg-red-50" iconColor="text-red-600" />
          <StatCard icon={TrendingUp} label="Repeat Users" value={fmt(data.repeat_users || 0)} iconBg="bg-blue-50" iconColor="text-blue-600" />
          <StatCard icon={Clock} label="No Order Users" value={fmt(data.no_order_users || 0)} iconBg="bg-gray-100" iconColor="text-gray-600" />
          <StatCard icon={ShoppingCart} label="Single Order Users" value={fmt(data.single_order_users || 0)} iconBg="bg-cyan-50" iconColor="text-cyan-600" />
        </StatSection>
      </div>

      {/* ── Orders chart ── */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 mb-6">
        <div className="flex items-center justify-between gap-4 mb-4">
          <h2 className="text-base font-semibold text-gray-800">Orders</h2>
          <Select value={trendRange} onValueChange={setTrendRange}>
            <SelectTrigger className="w-36 bg-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7_days">7 Days</SelectItem>
              <SelectItem value="1_month">1 Month</SelectItem>
              <SelectItem value="3_months">3 Months</SelectItem>
            </SelectContent>
          </Select>
        </div>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={trendData} barCategoryGap="25%" barGap={4}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
              <XAxis
                dataKey="day"
                tick={{ fontSize: 12, fill: "#6b7280" }}
                axisLine={false}
                tickLine={false}
                interval={trendRange === "1_month" ? 4 : 0}
              />
              <YAxis
                allowDecimals={false}
                tick={{ fontSize: 12, fill: "#6b7280" }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip
                contentStyle={{ borderRadius: 8, border: "1px solid #e5e7eb", fontSize: 13 }}
                cursor={{ fill: "#eef2fb" }}
              />
              <Bar dataKey="orders" fill="#0A4CAE" radius={[6, 6, 0, 0]} name="Orders" />
              <Bar dataKey="cancelled" fill="#ef4444" radius={[6, 6, 0, 0]} name="Cancelled" />
            </BarChart>
          </ResponsiveContainer>
      </div>

      {/* ── Doughnut charts ── */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-8">

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <h2 className="text-base font-semibold text-gray-800 mb-4">Orders by Status</h2>
          {hasOrderStatusData ? (
            <>
              <ResponsiveContainer width="100%" height={190}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={55}
                    outerRadius={85}
                    paddingAngle={3}
                    dataKey="value"
                  >
                    {pieData.map((entry) => (
                      <Cell key={entry.name} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{ borderRadius: 8, border: "1px solid #e5e7eb", fontSize: 13 }}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div className="grid grid-cols-2 gap-x-5 gap-y-2 border-t border-gray-100 pt-4 sm:grid-cols-3">
                {pieData.map((entry) => (
                  <div key={entry.name} className="flex items-center justify-between gap-2 text-xs">
                    <div className="flex min-w-0 items-center gap-2">
                      <span
                        className="h-2.5 w-2.5 flex-shrink-0 rounded-full"
                        style={{ backgroundColor: entry.color }}
                      />
                      <span className="truncate text-gray-600">{entry.name}</span>
                    </div>
                    <span className="font-bold text-gray-900">{fmt(entry.value)}</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <>
              <div className="flex items-center justify-center h-[190px] text-gray-400 text-sm">
                No order data yet
              </div>
              <div className="grid grid-cols-2 gap-x-5 gap-y-2 border-t border-gray-100 pt-4 sm:grid-cols-3">
                {pieData.map((entry) => (
                  <div key={entry.name} className="flex items-center justify-between gap-2 text-xs">
                    <div className="flex min-w-0 items-center gap-2">
                      <span className="h-2.5 w-2.5 flex-shrink-0 rounded-full" style={{ backgroundColor: entry.color }} />
                      <span className="truncate text-gray-600">{entry.name}</span>
                    </div>
                    <span className="font-bold text-gray-900">0</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <h2 className="text-base font-semibold text-gray-800 mb-4">Users</h2>
          {userPieData.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie
                  data={userPieData}
                  cx="50%"
                  cy="45%"
                  innerRadius={55}
                  outerRadius={85}
                  paddingAngle={3}
                  dataKey="value"
                >
                  {userPieData.map((entry) => (
                    <Cell key={entry.name} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #e5e7eb", fontSize: 13 }} />
                <Legend iconType="circle" iconSize={8} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-[220px] text-gray-400 text-sm">
              No user data yet
            </div>
          )}
        </div>
      </div>

      {/* ── Bottom Row ── */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">

        {/* Recent Orders */}
        <div className="xl:col-span-2 bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="px-6 py-4 border-b border-gray-100">
            <h2 className="text-base font-semibold text-gray-800">Recent Orders</h2>
          </div>
          {data.recent_orders.length === 0 ? (
            <div className="text-center py-10 text-gray-400 text-sm">No orders yet</div>
          ) : (
            <div className="divide-y divide-gray-50">
              {data.recent_orders.map((o) => (
                <div key={o.id} className="px-6 py-4 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-medium text-gray-900 text-sm truncate">{o.user_name}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{o.user_phone} · {fmtDate(o.created_at)}</p>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <span className="font-semibold text-gray-800 text-sm">{fmtRupee(o.total_amount)}</span>
                    <StatusBadge status={o.status} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Top Products */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
            <div className="px-6 py-4 border-b border-gray-100">
              <h2 className="text-base font-semibold text-gray-800">Top Products</h2>
            </div>
            {data.top_products.length === 0 ? (
              <div className="text-center py-8 text-gray-400 text-sm">No data yet</div>
            ) : (
              <div className="px-6 py-4 space-y-3">
                {data.top_products.map((p, i) => {
                  const maxQty = data.top_products[0]?.quantity || 1;
                  return (
                    <div key={i}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm text-gray-700 truncate max-w-[160px]">{p.name}</span>
                        <span className="text-xs font-semibold text-gray-500">{p.quantity} sold</span>
                      </div>
                      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-[#0A4CAE] rounded-full"
                          style={{ width: `${(p.quantity / maxQty) * 100}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
